import { type UseNavigateResult, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { useWebsocketContext } from '@/modules/websocketContext'
import type { DocumentDTO } from '../stores/types'
import { useDocument, useDocumentActions } from './document'

type Actions = ReturnType<typeof useDocumentActions>

type WebsocketAction = keyof typeof HANDLERS

interface EventData {
  action: WebsocketAction
  documents: DocumentDTO[]
  document: DocumentDTO | { id: number }
}

const HANDLERS = {
  FETCH_DOCUMENTS: (id: number, data: EventData, { setDocument }: Actions) => {
    const document = data.documents.find((doc) => doc.id === id)
    if (!document) return
    setDocument(document as DocumentDTO)
  },
  CREATE_DOCUMENT: () => {},
  UPDATE_DOCUMENT: (id: number, data: EventData, { setDocument }: Actions) => {
    if (id !== data.document.id) return
    setDocument(data.document as DocumentDTO)
  },
  DELETE_DOCUMENT: (
    id: number,
    data: EventData,
    _: Actions,
    navigate: UseNavigateResult<string>,
  ) => {
    if (id !== data.document.id) return
    navigate({ to: '/documents' })
  },
}

export const useDocumentWebsocket = () => {
  const navigate = useNavigate()
  const { isReady, subscribe, unsubscribe } = useWebsocketContext()
  const actions = useDocumentActions()
  const document = useDocument()

  useEffect(() => {
    if (!isReady) return

    const subscription = subscribe(
      { channel: 'AccountChannel' },
      {
        received: (data: unknown) => {
          const eventData = data as EventData
          const handler = HANDLERS[eventData.action]
          if (document?.id && handler) {
            handler(document.id, eventData, actions, navigate)
          }
        },
      },
    )
    return () => unsubscribe(subscription)
  }, [isReady])
}
